import React from "react";
import styled from "@emotion/styled";
import { colors } from "@ui/style/colors";
import { fonts } from "@ui/style/font";

// Styled Components
const Paragraph = styled.p`
	font-family: ${fonts.body};
	font-size: 18px;
	font-weight: 400;
	line-height: 1;
	color: ${colors.black};
	margin: 16px 0 0 0 !important;
`;

const BlocksParagraphRenderer: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
	<Paragraph>{children}</Paragraph>
);

const HeadingBase = styled.h1`
	font-family: ${fonts.header};
	color: ${colors.black};
	font-weight: 700;
	line-height: 1;
	margin-bottom: 24px;
	margin-top: 16px;
`;

const Heading1 = styled(HeadingBase)`
	font-size: 24px;
	line-height: 1.3;
`;

const Heading2 = styled(HeadingBase)`
	font-size: 24px;
	line-height: 1.3;
`;

const Heading3 = styled(HeadingBase)`
	font-size: 24px;
	line-height: 1.3;
`;

const Heading4 = styled(HeadingBase)`
	font-size: 24px;
	line-height: 1.3;
`;

const Heading5 = styled(HeadingBase)`
	font-size: 24px;
	line-height: 1.3;
`;

const Heading6 = styled(HeadingBase)`
	font-size: 24px;
	line-height: 1.3;
`;

const HeadingDefault = styled(Paragraph)`
	font-weight: 700;
`;

const OrderedList = styled.ol`
	font-family: ${fonts.body};
	font-size: 18px;
	line-height: 1.7;
	color: ${colors.black};
	padding-left: 24px;
	margin: 0 0 24px 0;

	&:first-of-type {
		margin-top: 0;
	}
`;

const UnorderedList = styled.ul`
	font-family: ${fonts.body};
	font-size: 16px;
	line-height: 8px;
	color: ${colors.grey};
	padding-left: 12px;
	margin: 0;
	margin-top: 24px;
`;

const ListItem = styled.li`
	font-family: ${fonts.body};
	color: ${colors.primaryDark};
`;

const StyledImage = styled.img`
	max-width: 100%;
	height: auto;
	border-radius: 8px;
`;

const Quote = styled.blockquote`
	border-left: 4px solid ${colors.primary};
	margin: 0;
	padding: 8px 16px;
	color: ${colors.grey80};
	font-style: italic;
`;

const CodeBlock = styled.pre`
	margin: 0;
	padding: 12px;
	border-radius: 8px;
	background-color: ${colors.grey10};
	font-family: "Source Code Pro", monospace;
	overflow-x: auto;
`;

const InlineCode = styled.code`
	font-family: "Source Code Pro", monospace;
	background-color: ${colors.grey10};
	padding: 0 4px;
	border-radius: 4px;
`;

const HyperlinkContainer = styled.a`
	display: flex;
	flex-direction: row;
	align-items: center;
	margin-bottom: 10px;
	text-decoration: none;
	gap: 10px;

	&:hover {
		opacity: 0.8;
	}
`;

const HyperlinkImage = styled.img`
	width: 45px;
	height: 45px;
	border-radius: 24px;
	object-fit: contain;
`;

const HyperlinkTitle = styled.span`
	font-family: ${fonts.body};
	font-size: 18px;
	font-weight: 700;
	color: ${colors.primaryDark50};
	line-height: 1.5;
`;

const BoldText = styled.strong`
	font-weight: 700;
`;

const ItalicText = styled.em`
	font-style: italic;
`;

const UnderlineText = styled.span`
	text-decoration: underline;
`;

// Common Blocks Overrides
export const commonBlocksOverrides = {
	paragraph: BlocksParagraphRenderer,
	heading: ({ children, level }: { children?: React.ReactNode; level: 1 | 2 | 3 | 4 | 5 | 6 }): React.ReactElement => {
		switch (level) {
			case 1:
				return <Heading1>{children}</Heading1>;
			case 2:
				return <Heading2>{children}</Heading2>;
			case 3:
				return <Heading3>{children}</Heading3>;
			case 4:
				return <Heading4>{children}</Heading4>;
			case 5:
				return <Heading5>{children}</Heading5>;
			case 6:
				return <Heading6>{children}</Heading6>;
			default:
				return <HeadingDefault>{children}</HeadingDefault>;
		}
	},
	list: ({ children, format }: { children?: React.ReactNode; format: "ordered" | "unordered" }): React.ReactElement =>
		format === "ordered" ? (
			<OrderedList>{children}</OrderedList>
		) : (
			<UnorderedList id="listUnOrdered">{children}</UnorderedList>
		),
	"list-item": ({ children }: { children?: React.ReactNode }): React.ReactElement => (
		<ListItem id="listItem">{children}</ListItem>
	),

	image: ({
		image,
	}: {
		image?: { url?: string; alternativeText?: string | null; width?: number; height?: number } | null;
	}): React.ReactElement | null => {
		if (!image?.url) {
			return null;
		}
		return <StyledImage src={image.url} alt={image.alternativeText ?? ""} width={image.width} height={image.height} />;
	},
	quote: ({ children }: { children?: React.ReactNode }): React.ReactElement => <Quote>{children}</Quote>,
	code: ({ plainText }: { plainText?: string }): React.ReactElement => <CodeBlock>{plainText}</CodeBlock>,
	link: ({ children, url }: { children?: React.ReactNode; url: string }): React.ReactElement => (
		<HyperlinkContainer>
			<HyperlinkImage src={url} />
			<HyperlinkTitle>{children}</HyperlinkTitle>
		</HyperlinkContainer>
	),
};

// Common Modifiers Overrides
export const commonModifiersOverrides = {
	bold: ({ children }: { children?: React.ReactNode }): React.ReactElement => <BoldText>{children}</BoldText>,
	italic: ({ children }: { children?: React.ReactNode }): React.ReactElement => <ItalicText>{children}</ItalicText>,
	underline: ({ children }: { children?: React.ReactNode }): React.ReactElement => (
		<UnderlineText>{children}</UnderlineText>
	),
	code: ({ children }: { children?: React.ReactNode }): React.ReactElement => <InlineCode>{children}</InlineCode>,
};

// Announcement-specific Blocks Overrides (without custom link styling)
export const announcementBlocksOverrides = {
	...commonBlocksOverrides,
	link: ({ children, url }: { children?: React.ReactNode; url: string }): React.ReactElement => (
		<a href={url} target="_blank" rel="noopener noreferrer">
			{children}
		</a>
	),
};
